import type { FolderSummary } from '../types/folder.types';

import type { FolderSearchPage } from './fetchers/folder';
import type { MyFoldersVariables } from './folder.queries';

/**
 * 내 폴더 검색 응답을 이 세션에서 아는 변경과 맞춘다.
 *
 * 내 폴더 검색(`/quizset-folders/my/search`)은 검색 인덱스를 읽어 원본보다 1초 안팎 늦다. 만들거나 지운
 * 직후의 응답에는 새 폴더가 빠지고 지운 폴더가 되살아난다. 결과를 알고 있으므로 응답 쪽에서 바로잡는다 —
 * 캐시를 고친 뒤 늦은 응답이 그걸 덮어써도 목록이 되돌아가지 않는다. 캐시 쪽은 `folderListCache`.
 */

/** 이 세션에서 지운 폴더. id 는 다시 쓰이지 않으므로 세션 동안 걸러도 안전하다. */
const deletedFolderIds = new Set<string>();

/**
 * 만든 폴더를 응답에 끼워 주는 시간. 인덱스 지연(1초 안팎)을 넉넉히 덮으면 된다.
 *
 * 영원히 기억하지 않는다 — 로그아웃은 전체 리로드가 아니라 모듈 메모리가 남고, 다른 계정의 목록에는
 * 이 폴더가 없으니 계속 끼우게 된다. 시간이 지나면 잊는다.
 */
const CREATED_FOLDER_RECONCILE_TTL_MS = 15_000;

/**
 * 이 세션에서 만들었는데 아직 검색 응답에서 본 적 없는 폴더. 응답에 한 번 실리면 인덱스가 따라온 것이므로
 * 잊는다 — 계속 끼우면 뒤 페이지와 겹칠 수 있다.
 */
const unseenCreatedFolders = new Map<
  string,
  { folder: FolderSummary; rememberedAt: number }
>();

export const rememberDeletedFolder = (id: string) => {
  deletedFolderIds.add(id);
  unseenCreatedFolders.delete(id);
};

export const rememberCreatedFolder = (folder: FolderSummary) => {
  unseenCreatedFolders.set(folder.id, { folder, rememberedAt: Date.now() });
};

/** 아직 끼워 줄 만큼 최근에 만든 폴더만. 나중에 만든 것이 앞이다. */
const takeFreshCreatedFolders = () => {
  const now = Date.now();
  for (const [id, entry] of unseenCreatedFolders) {
    if (now - entry.rememberedAt > CREATED_FOLDER_RECONCILE_TTL_MS)
      unseenCreatedFolders.delete(id);
  }
  return [...unseenCreatedFolders.values()]
    .map(entry => entry.folder)
    .reverse();
};

type ListShape = Pick<MyFoldersVariables, 'order' | 'categoryFilters'>;

/** 만든 폴더를 맨 앞에 둘 수 있는 목록인가 — 최신순이고 검색어가 없을 때. 인기순은 자리를 모른다. */
export const canPrependCreatedFolder = (variables: ListShape | undefined) =>
  variables?.order !== 'POPULARITY' &&
  !variables?.categoryFilters?.criteria?.searchText;

/**
 * 검색 응답 한 페이지를 바로잡는다. 인덱스가 따라온 뒤의 응답에는 아무것도 바꾸지 않는다.
 *
 * - 지운 폴더가 실려 있으면 빼고 전체 개수도 그만큼 줄인다.
 * - 만든 폴더가 첫 페이지에 없으면 맨 앞에 끼우고 전체 개수를 그만큼 올린다.
 */
export const reconcileFolderSearchPage = (
  page: FolderSearchPage,
  context: { variables?: ListShape; isFirstPage: boolean },
): FolderSearchPage => {
  let items = page.items;
  let delta = 0;

  if (deletedFolderIds.size > 0) {
    items = items.filter(item => !deletedFolderIds.has(item.id));
    delta -= page.items.length - items.length;
  }

  for (const item of items) unseenCreatedFolders.delete(item.id);
  if (context.isFirstPage && canPrependCreatedFolder(context.variables)) {
    const created = takeFreshCreatedFolders();
    items = [...created, ...items];
    delta += created.length;
  }

  if (delta === 0) return page;
  return {
    ...page,
    items,
    totalCount:
      page.totalCount === undefined
        ? undefined
        : Math.max(0, page.totalCount + delta),
  };
};
